/**
 * Row primitives for the payment-types screens — one look on every surface.
 * Ruby on light; green only for success. Matches the approved mockup's
 * `.row`, `.row.sub`, `.pill`, `.scope` and `.sw` (44px hit target).
 */
import React from 'react';

// ---------------------------------------------------------------------------
// Switch — 44px hit target, ruby on, visibly disabled, striped when locked
// ---------------------------------------------------------------------------

export interface SwitchProps {
  on: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  /** Held by a rule (override, surcharge ⇒ debit, no approval) — striped track. */
  locked?: boolean;
  label: string;
  /** Dark surfaces (the black Admin card, the save bar). */
  dark?: boolean;
}

export const Switch: React.FC<SwitchProps> = ({ on, onChange, disabled, locked, label, dark }) => {
  const off = dark ? 'bg-stone-600' : 'bg-stone-300';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={() => { if (!disabled && onChange) onChange(!on); }}
      className="group relative inline-flex h-11 w-[52px] shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-[3px] focus-visible:ring-rose-200 disabled:cursor-not-allowed"
    >
      <span
        aria-hidden
        className={`relative block h-6 w-11 rounded-full transition-colors ${on ? 'bg-ruby' : off} ${disabled ? 'opacity-40' : ''}`}
        style={locked ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0 4px, transparent 4px 8px)' } : undefined}
      >
        <span
          className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-[left] ${on ? 'left-[23px]' : 'left-[3px]'}`}
        />
      </span>
    </button>
  );
};

// ---------------------------------------------------------------------------
// Pills and scope badges
// ---------------------------------------------------------------------------

type PillTone = 'neutral' | 'follow' | 'lock' | 'ok' | 'off' | 'warn';
const PILL: Record<PillTone, string> = {
  neutral: 'bg-stone-100 text-stone-600',
  follow: 'bg-blue-50 text-blue-700',
  lock: 'bg-stone-100 text-stone-600',
  ok: 'bg-green-50 text-green-700',
  off: 'bg-red-50 text-red-700',
  warn: 'bg-amber-50 text-amber-800',
};

export const Pill: React.FC<{ tone?: PillTone; title?: string; children: React.ReactNode }> = ({ tone = 'neutral', title, children }) => (
  <span title={title} className={`whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${PILL[tone]}`}>{children}</span>
);

export const ScopeBadge: React.FC<{ kind: 'reg' | 'loc' | 'adm'; small?: boolean; children: React.ReactNode }> = ({ kind, small, children }) => {
  const cls = kind === 'reg'
    ? 'border border-rose-200 bg-[#FFF1F3] text-ruby-deep'
    : kind === 'loc'
      ? 'border border-blue-200 bg-blue-50 text-blue-700'
      : 'bg-stone-900 text-white';
  return (
    <span className={`whitespace-nowrap rounded-full font-semibold ${small ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'} ${cls}`}>
      {children}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Card + rows
// ---------------------------------------------------------------------------

export const Card: React.FC<{
  title: React.ReactNode; sub?: React.ReactNode; badge?: React.ReactNode; dark?: boolean; children: React.ReactNode;
}> = ({ title, sub, badge, dark, children }) => (
  <section className={`mb-4 overflow-hidden rounded-2xl border bg-white ${dark ? 'border-stone-900' : 'border-stone-200'}`}>
    <header className={`flex items-center justify-between gap-3 border-b px-[18px] py-4 ${dark ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-200'}`}>
      <div className="min-w-0">
        <h3 className="m-0 text-[15px] font-semibold">{title}</h3>
        {sub && <div className={`mt-0.5 text-xs ${dark ? 'text-stone-400' : 'text-stone-600'}`}>{sub}</div>}
      </div>
      {badge}
    </header>
    <div>{children}</div>
  </section>
);

export const Icon: React.FC<{ on?: boolean; small?: boolean; children: React.ReactNode }> = ({ on, small, children }) => (
  <div className={`grid shrink-0 place-items-center rounded-[10px] ${small ? 'h-7 w-7 bg-transparent text-stone-500' : `h-[34px] w-[34px] ${on ? 'bg-[#FFF1F3] text-ruby' : 'bg-stone-100 text-stone-600'}`}`}>
    {children}
  </div>
);

/** A top-level row: tender, admin grant, or location value. */
export const Row: React.FC<{
  icon: React.ReactNode; iconOn?: boolean; title: React.ReactNode; desc?: React.ReactNode;
  right?: React.ReactNode; testId?: string; children?: React.ReactNode;
}> = ({ icon, iconOn, title, desc, right, testId, children }) => (
  <div data-testid={testId} className="flex min-h-[60px] items-center gap-3 border-t border-stone-100 px-[18px] py-3 first:border-t-0">
    <Icon on={iconOn}>{icon}</Icon>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-stone-900">{title}</div>
      {desc && <div className="mt-0.5 text-xs leading-[1.45] text-stone-600">{desc}</div>}
      {children}
    </div>
    {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
  </div>
);

/** A setting that hangs off the tender above it. */
export const SubRow: React.FC<{
  icon: React.ReactNode; title: React.ReactNode; badge?: React.ReactNode; desc?: React.ReactNode; right?: React.ReactNode; testId?: string;
}> = ({ icon, title, badge, desc, right, testId }) => (
  <div data-testid={testId} className="flex min-h-[52px] items-center gap-3 border-t border-stone-100 bg-stone-50 py-3 pl-[58px] pr-[18px]">
    <Icon small>{icon}</Icon>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-stone-900">{title} {badge}</div>
      {desc && <div className="mt-0.5 text-xs leading-[1.45] text-stone-600">{desc}</div>}
    </div>
    {right && <div className="flex shrink-0 items-center gap-2">{right}</div>}
  </div>
);

export const Warn: React.FC<{ children: React.ReactNode }> = ({ children }) => <span className="text-amber-700">{children}</span>;
export const Bad: React.FC<{ children: React.ReactNode }> = ({ children }) => <span className="text-ruby-deep">{children}</span>;

/** A small text action beside a switch — "Follow location". 44px hit target. */
export const TextAction: React.FC<{ onClick: () => void; disabled?: boolean; testId?: string; title?: string; children: React.ReactNode }> = ({ onClick, disabled, testId, title, children }) => (
  <button
    type="button"
    data-testid={testId}
    title={title}
    onClick={onClick}
    disabled={disabled}
    className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-lg px-2 text-xs font-medium text-ruby hover:text-ruby-deep hover:underline disabled:cursor-not-allowed disabled:opacity-40"
  >
    {children}
  </button>
);
